import { useEffect } from 'react';
import { useGoogleOAuthConnection } from '../../hooks/useGoogleOAuthConnection';

function ConnectedGoogleAccount({ connection, isDisconnecting, onDisconnect }) {
  return (
    <div className="rounded-sm border border-emerald-800/40 bg-emerald-900/20 p-3">
      <div className="text-xs uppercase tracking-wide text-emerald-400">Compte Google connecté</div>
      <div className="mt-1 text-sm text-white">{connection.email}</div>
      {connection.name ? <div className="text-xs text-zinc-400">{connection.name}</div> : null}
      <button
        type="button"
        data-testid="crawl-google-disconnect-button"
        onClick={onDisconnect}
        disabled={isDisconnecting}
        className="mt-3 rounded-sm border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-white hover:text-white disabled:opacity-50"
      >
        {isDisconnecting ? 'Déconnexion...' : 'Déconnecter le compte Google'}
      </button>
    </div>
  );
}

function ConnectGoogleAccount({ isConnecting, onConnect }) {
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-950/50 p-3">
      <div className="text-xs uppercase tracking-wide text-zinc-500">Compte Google applicatif</div>
      <p className="mt-1 text-xs text-zinc-400">Permet de rattacher un compte Google à l'application pour les futurs services Google directs.</p>
      <button
        type="button"
        data-testid="crawl-google-connect-button"
        onClick={onConnect}
        disabled={isConnecting}
        className="mt-3 rounded-sm bg-white px-3 py-2 text-xs font-medium text-black transition-colors hover:bg-zinc-200 disabled:opacity-50"
      >
        {isConnecting ? 'Connexion...' : 'Connecter un compte Google'}
      </button>
    </div>
  );
}

function GoogleOAuthSection({ googleConnection }) {
  if (googleConnection.connection.connected) {
    return (
      <ConnectedGoogleAccount
        connection={googleConnection.connection}
        isDisconnecting={googleConnection.isDisconnecting}
        onDisconnect={googleConnection.disconnect}
      />
    );
  }
  return <ConnectGoogleAccount isConnecting={googleConnection.isConnecting} onConnect={googleConnection.connect} />;
}

function GoogleCrawlCredentials({ googleEmail, setGoogleEmail, googlePassword, setGooglePassword, crawling, loading, useSavedGoogleSession }) {
  return (
    <>
      <div className="space-y-2">
        <label htmlFor="google-email" className="text-sm font-medium">Compte Google utilisé pour le crawl</label>
        <input id="google-email" data-testid="crawl-google-email-input" type="email" value={googleEmail}
          onChange={(event) => setGoogleEmail(event.target.value)} placeholder="email Google autorisé"
          disabled={crawling || loading}
          className="h-10 w-full rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 text-white placeholder:text-zinc-600 transition-colors duration-300 focus:border-white focus:outline-none focus:ring-0" />
      </div>
      <div className="space-y-2">
        <label htmlFor="google-password" className="text-sm font-medium">Mot de passe Google</label>
        <input id="google-password" data-testid="crawl-google-password-input" type="password" value={googlePassword}
          onChange={(event) => setGooglePassword(event.target.value)} placeholder="Utilisé uniquement pour établir la session du site cible"
          disabled={crawling || useSavedGoogleSession}
          className="h-10 w-full rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 text-white placeholder:text-zinc-600 transition-colors duration-300 focus:border-white focus:outline-none focus:ring-0 disabled:opacity-50" />
      </div>
    </>
  );
}

function GoogleSessionOptions({ useSavedGoogleSession, setUseSavedGoogleSession, rememberGoogleSession, setRememberGoogleSession, crawling }) {
  return (
    <>
      <label className="flex items-center gap-2 text-xs text-zinc-400">
        <input type="checkbox" data-testid="crawl-google-use-saved-session" checked={useSavedGoogleSession}
          onChange={(event) => setUseSavedGoogleSession(event.target.checked)} disabled={crawling} />
        Réutiliser une session Google enregistrée si disponible
      </label>
      <label className="flex items-center gap-2 text-xs text-zinc-400">
        <input type="checkbox" data-testid="crawl-google-remember-session" checked={rememberGoogleSession}
          onChange={(event) => setRememberGoogleSession(event.target.checked)} disabled={crawling} />
        Enregistrer la session du site cible pour les prochains crawls
      </label>
      <p className="text-xs text-yellow-600">Le compte Google connecté à l'application servira aux futurs services Google directs. Le mot de passe saisi ici n'est pas stocké.</p>
    </>
  );
}

export function CrawlGoogleFields({
  googleEmail, setGoogleEmail, googlePassword, setGooglePassword,
  rememberGoogleSession, setRememberGoogleSession,
  useSavedGoogleSession, setUseSavedGoogleSession, crawling,
}) {
  const googleConnection = useGoogleOAuthConnection((connectedEmail) => {
    if (!googleEmail) {
      setGoogleEmail(connectedEmail);
    }
  });

  useEffect(() => {
    if (googleConnection.connection.connected && googleConnection.connection.email && !googleEmail) {
      setGoogleEmail(googleConnection.connection.email);
    }
  }, [googleConnection.connection, googleEmail, setGoogleEmail]);

  return (
    <div className="space-y-4">
      <GoogleOAuthSection googleConnection={googleConnection} />
      <GoogleCrawlCredentials googleEmail={googleEmail} setGoogleEmail={setGoogleEmail}
        googlePassword={googlePassword} setGooglePassword={setGooglePassword}
        crawling={crawling} loading={googleConnection.loading} useSavedGoogleSession={useSavedGoogleSession} />
      <GoogleSessionOptions useSavedGoogleSession={useSavedGoogleSession} setUseSavedGoogleSession={setUseSavedGoogleSession}
        rememberGoogleSession={rememberGoogleSession} setRememberGoogleSession={setRememberGoogleSession} crawling={crawling} />
    </div>
  );
}
